import {
  BasicChartDataSource,
  BasicCartesianChartConfig,
  AggregateCartesianChartConfig,
  AggregateCartesianChartDataConfig,
  BasicPieChartConfig,
  BasicPolarChartConfig,
  BasicBarChartConfig,
} from '@app/data/interface/widget-config';
import {
  BasicBarChartColourPalette,
  BasicBarChartEchartConfigTemplate,
  BasicBarChartYAxisTemplate,
} from '@app/data/widget-config-templates/basic-bar-chart';
import {
  BasicCartesianChartEchartConfigTemplate,
  BasicCartesianChartAxisTemplate,
} from '@app/data/widget-config-templates/basic-cartesian-chart';
import {
  dataViewFormatter,
  formatAxisPointerLabelDecimal,
  formatChartTooltip,
  formatLongLabel,
  getTimestampedFileNameForWidget,
} from '@app/widgets/helpers/widgets';
import { EChartsOption, SeriesOption } from 'echarts';
import * as echarts from 'echarts/core';
import { YAXisOption } from 'echarts/types/dist/shared';
import moment from 'moment';
import { formatAxisPointerLabelPercent, hexToRgba } from '@app/widgets/helpers/widgets';
import { lizardSurveyTooltipFormatter, mapLizardSurveyData } from './widget-helpers/lizard-widgets';
import {
  wetlandsVegetationSpeciesMapper,
  formatWetlandVegetationSpeciesDV,
  mapTerrestrialVegetationData,
  terrestrialVegetationTooltipFormatter,
} from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/vegetation-widgets';
import {
  wetlandsBirdSpeciesMapper,
  formatWetlandBirdSpeciesDV,
} from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/wetland-bird-widgets';
import { mapTerrestrialBirdSpeciesData } from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/terrestrial-bird-widgets';
import {
  buildAllowedIdentifierSet,
  buildSourceIdToIdentifierMap,
  resolveNormalizedSourceIdentifier,
} from '@app/client/clients/wastemanagement/taiao-dashboard-layout/widget-helpers/source-filter-utils';
import { color } from './colors';
import { buildPeriphytonCoverCompositionConfig } from './widget-helpers/periphyton-widgets';

import { convertFrogData, showAllNotEmptyValuesFormatter } from './widget-helpers/hochsetter-frog';

/**
 * Locations and Sources for Ground Water
 */
const locationsAndSourcesGW = [
  { label: 'BH01', source: 'arl_bh01' },
  { label: 'BH10', source: 'arl_bh10' },
  { label: 'BH13', source: 'arl_bh13' },
  { label: 'BH14', source: 'arl_bh14' },
  { label: 'BH2', source: 'arl_bh2' },
  { label: 'BH3', source: 'arl_bh3' },
  { label: 'BH5', source: 'arl_bh5' },
  { label: 'BH6', source: 'arl_bh6' },
  { label: 'BH7', source: 'arl_bh7' },
  { label: 'BH9', source: 'arl_bh9' },
  { label: 'Dup', source: 'arl_dup' },
  { label: 'DUP01', source: 'arl_dup01' },
  { label: 'DUP1', source: 'arl_dup1' },
  { label: 'TB01', source: 'arl_tb01' },
];

/**
 * Wind direction compass labels.
 */
const windDirectionCompassLabels = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
];

/**
 * Identifier mapping for the weather chart analytes.
 */
export const weatherAnalyteId = {
  temperature: 'sample-temperature-c',
  precipitation: 'precipitation',
  windSpeed: 'wind-speed',
  windDirection: 'wind-direction',
};

/**
 * Weather data from NIWA specific to Waste Management
 */
export const sourcesWeatherWM = [
  { source: 'niwa_wayby_valley_waste_management', analyteId: 'sample-temperature-c' },
  { source: 'niwa_wayby_valley_waste_management', analyteId: 'precipitation' },
];

/**
 * Wind data from NIWA specific to Waste Management
 */
export const sourcesWindWM = [
  { source: 'niwa_wayby_valley_waste_management', analyteId: 'wind-speed' },
  { source: 'niwa_wayby_valley_waste_management', analyteId: 'wind-direction' },
];

const analytesForGroundwater = [
  { analyte: 'chemical-oxygen-demand', name: 'COD as O2', unit: 'g/m³' },
  { analyte: 'electrical-conductivity-in-lab', name: 'Electrical Conductivity (lab)', unit: 'mS/m' },
  { analyte: 'sample-temperature-c', name: 'Temperature', unit: '°C' },
  { analyte: 'ph-in-lab', name: 'pH', unit: '' },
  { analyte: 'total-alkalinity', name: 'Alkalinity', unit: 'g/m³' },
  { analyte: 'dissolved-arsenic', name: 'Arsenic', unit: 'g/m³' },
  { analyte: 'dissolved-copper', name: 'Copper', unit: 'g/m³' },
  { analyte: 'dissolved-iron', name: 'Iron', unit: 'g/m³' },
  { analyte: 'dissolved-lead', name: 'Lead', unit: 'g/m³' },
  { analyte: 'dissolved-zinc', name: 'Zinc', unit: 'g/m³' },
  { analyte: 'dissolved-magnesium', name: 'Magnesium', unit: 'g/m³' },
  { analyte: 'dissolved-manganese', name: 'Manganese', unit: 'g/m³' },
  { analyte: 'dissolved-nickel', name: 'Nickel', unit: 'g/m³' },
  { analyte: 'nitrate-+-nitrite-nitrogen', name: 'Nitrate Nitrogen', unit: 'g/m³' },
  { analyte: 'dissolved-potassium', name: 'Potassium', unit: 'g/m³' },
  { analyte: 'total-sodium', name: 'Sodium', unit: 'g/m³' },
  { analyte: 'sulphate', name: 'Sulphate', unit: 'g/m³' },
  { analyte: 'total-boron', name: 'Boron', unit: 'g/m³' },
  { analyte: 'total-calcium', name: 'Calcium', unit: 'g/m³' },
  { analyte: 'chloride', name: 'Chloride', unit: 'g/m³' },
  { analyte: 'total-ammoniacal-nitrogen', name: 'Total Ammoniacal Nitrogen', unit: 'g/m³' },
  { analyte: 'total-hardness', name: 'Total Hardness', unit: 'g/m³' },
  { analyte: 'bicarbonate-at-25-degrees-c', name: 'Bicarbonate', unit: 'g/m³' },
  { analyte: 'sum-of-anions', name: 'Sum of Anions', unit: 'g/m³' },
  { analyte: 'sum-of-cations', name: 'Sum of Cations', unit: 'g/m³' },
];

const locationsAndSourcesQMCI = [
  { label: 'Ec1', source: 'arl_ec1' },
  { label: 'Ec2', source: 'arl_ec2' },
  { label: 'Ec3', source: 'arl_ec3' },
  { label: 'Ec4', source: 'arl_ec4' },
  { label: 'Ec5', source: 'arl_ec5' },
];

const sourcesMacrophyteLocations = [{ label: 'Ec5', source: 'arl_ec5' }];

const sourcesVegetationGrid = [
  { label: 'B5', source: 'arl_b5' },
  { label: 'G6', source: 'arl_g6' },
  { label: 'H6', source: 'arl_h6' },
  { label: 'H8', source: 'arl_h8' },
  { label: 'J13', source: 'arl_j13' },
  { label: 'N13', source: 'arl_n13' },
  { label: 'N15', source: 'arl_n15' },
  { label: 'N4', source: 'arl_n4' },
  { label: 'N5', source: 'arl_n5' },
  { label: 'O15', source: 'arl_o15' },
  { label: 'O17', source: 'arl_o17' },
  { label: 'O4', source: 'arl_o4' },
  { label: 'O5', source: 'arl_o5' },
  { label: 'P4', source: 'arl_p4' },
  { label: 'P5', source: 'arl_p5' },
  { label: 'Q5', source: 'arl_q5' },
  { label: 'R6', source: 'arl_r6' },
  { label: 'R7', source: 'arl_r7' },
  { label: 'R8B', source: 'arl_r8b' },
  { label: 'S11', source: 'arl_s11' },
  { label: 'S9', source: 'arl_s9' },
  { label: 'V4', source: 'arl_v4' },
  { label: 'W8', source: 'arl_w8' },
  { label: 'X7', source: 'arl_x7' },
];

const WetlandsVegetationSpecies = [
  'kahikatea',
  'ti-kōuka',
  'myrsine-australis-māpou',
  'coprosma-robusta-karamu',
  'hangehange',
  'carpodetus-serratus',
  'leptospermum-scoparium-mānuka',
  'totara',
  'freycinetia-banksii',
  'coprosma-tenuicaulis',
  'dicksonia-squarrosa-whekī',
  'freycinetia-banksii-grey-willow',
  'lancewood',
  'wood-dead',
  'gorse',
  'melicytus-ramiflorus-māhoe',
  'ponga',
  'pittosporum-eugenioides',
  'pittosporum-tenuifolium',
  'maire',
  'tānekaha',
  'coprosma-rhamnoides',
  'blackberry',
];

const locationsAndSourcesARLSediment = [
  { label: 'Ec1A', source: 'arl_ec1a' },
  { label: 'Ec6A', source: 'arl_ec6a' },
  { label: 'Ec6B', source: 'arl_ec6b' },
  { label: 'Ec7A', source: 'arl_ec7a' },
  { label: 'Ec7B', source: 'arl_ec7b' },
];

const terrestrialVegetationSpecies = [
  'Nestegis-cunninghamii-black-maire',
  'Nestegis-lanceolata-white-maire',
  'acacia-mearnsii-black-wattle',
  'alectryon-excelsus-titoki',
  'beilschmiedia-tarairi',
  'beilschmiedia-tawa',
  'brachglottis-repanda',
  'carmichaelia-australis-nz-broom',
  'carpodetus-serratus',
  'coprosma-arborea-mamangi',
  'coprosma-areolata',
  'coprosma-autumnalis-kanono',
  'coprosma-grandiflora-raurekau',
  'coprosma-lucida',
  'coprosma-rhamnoides',
  'coprosma-rigida',
  'coprosma-robusta-karamu',
  'coprosma-rotundifolia',
  'coprosma-sp',
  'coprosma-spathulata-hikuruana',
  'coprosma-tenuicaulis',
  'cordyline-banksii-ti-ngahere',
  'elaeocarpus-dentatus-hinau',
  'fuchsia-excorticata-kotukutuku',
  'gorse',
  'hakea-salicifolia-willow-leaf-hakea',
  'hakea-sericea-silky-hakea',
  'hangehange',
  'houheria-sexstylosa',
  'kahikatea',
  'kaikōmako',
  'karaka',
  'kauri',
  'kohekohe',
  'kohurangi',
  'kānuka',
  'lancewood',
  'leptospermum-scoparium-mānuka',
  'libocedrus-plumosa-kawaka',
  'ligustrum-lucidum-glossy-privet',
  'ligustrum-sinense-chinese-privet',
  'maire',
  'melicytus-macrophyllus-mahoe',
  'melicytus-micranthus-mahoe',
  'melicytus-ramiflorus-māhoe',
  'mida-salicifolia-maire-taiki',
  'mingimingi',
  'miro',
  'myrsine-australis-māpou',
  'myrsine-salicina-toro',
  'nīkau',
  'olearia-furfuracea',
  'olearia-rani-heketara',
  'pigeonwood',
  'pinus-radiata-radiata-pine',
  'pittosporum-tenuifolium',
  'podocarpus-laetus-halls-totara',
  'prumnopitys-taxifolia',
  'pseudopanax-arboreus-five-finger-puahou',
  'pseudopanax-lessonii',
  'pterophylla-sylvicola-towai',
  'pukatea',
  'pūriri-tree',
  'rewarewa',
  'rhabdothamnus-solandri-gloxinia-taurepo',
  'rimu',
  'schefflera-digitata-pate',
  'solanum-mauritianum',
  'streblus-heterophyllus-milk-tree',
  'ti-kōuka',
  'toropapa',
  'totara',
  'tānekaha',
  'vegetation-unknown',
  'wood-dead',
];

const terrestrialBirdSpecies = [
  '#N/A',
  'bird-unknown',
  'blackbird',
  'chaffinch-pahirini',
  'fernbird',
  'grey-warbler',
  'kahu',
  'kererū',
  'kingfisher',
  'koromiko',
  'kākā-(parrot)',
  'kōmiromiro',
  'morepork',
  'mynah',
  'paradise-shellduck',
  'pīwakawaka-(fantail)',
  'rosella-cockatoo',
  'shining-cuckoo',
  'silver-eye',
  'spotless-crake',
  'spur-winged-plover',
  'tui',
  'welcome-swallow-warou',
];

/**
 * Locations and Sources for Surface Water
 *
 * Some locations are commented out as they are not accessible via HydroVu API
 */
const locationsAndSourcesSW = [
  { label: 'SW1A (AquaTROLL)', source: 'arl_sw1a_aq', default: false },
  { label: 'SW1A Raw Data', source: 'arl_sw1a_manual', default: true },
  { label: 'SW2A (VuLink)', source: 'arl_sw2a_vu', default: false },
  { label: 'SW2A Raw Data', source: 'arl_sw2a_manual', default: true },
  { label: 'SW3A (VuLink)', source: 'arl_sw3a_vu', default: false },
  { label: 'SW3A Raw Data', source: 'arl_sw3a_manual', default: true },
  { label: 'SW4A (AquaTROLL)', source: 'arl_sw4a_aq', default: false },
  { label: 'SW4A Raw Data', source: 'arl_sw4a_manual', default: true },
  { label: 'H10 (AquaTROLL)', source: 'arl_h10', default: false },
  { label: 'H10 (VuLink)', source: 'arl_h10_vu', default: false },
  { label: 'H10 Raw Data', source: 'arl_h10_manual', default: true },
  { label: 'H11 (AquaTROLL)', source: 'arl_h11_aq', default: false },
  { label: 'H11 (VuLink)', source: 'arl_h11', default: false },
  { label: 'H11 Raw Data', source: 'arl_h11_manual', default: true },
];

/**
 * Locations and Sources for Stream Flow
 *
 */
const locationsAndSourcesSF = [
  { label: 'SF1', source: 'arl_sf1' }, // Surface Flow 1
  { label: 'SF2', source: 'arl_sf2' }, // Surface Flow 2
  { label: 'SF3', source: 'arl_sf3' }, // Surface Flow 3
  { label: 'SF4', source: 'arl_sf4' }, // Surface Flow 4
];

/**
 * Ground Water Source IDs for ARL Hoteo site
 */
const gwSourceIdsARLHoteo = [
  { label: 'BH01', source: 'arl_bh01' },
  { label: 'BH2', source: 'arl_bh2' },
  { label: 'BH3', source: 'arl_bh3' },
  { label: 'BH4', source: 'arl_bh4' },
  { label: 'BH5', source: 'arl_bh5' },
  { label: 'BH6', source: 'arl_bh6' },
  { label: 'BH7', source: 'arl_bh7' },
  { label: 'BH8', source: 'arl_bh8' },
  { label: 'BH9', source: 'arl_bh9' },
  { label: 'BH10', source: 'arl_bh10' },
  { label: 'BH11', source: 'arl_bh11' },
  { label: 'BH12', source: 'arl_bh12' },
  { label: 'BH13', source: 'arl_bh13' },
  { label: 'BH14', source: 'arl_bh14' },
  { label: 'TB01', source: 'arl_tb01' },
  { label: 'BH20', source: 'arl_bh20' },
];

/**
 * Surface Water Analyte Properties
 */
const analytesForSurfacewater = [
  { analyte: 'turbidity', name: 'Turbidity', unit: 'NTU', default: false },
  // { analyte: 'turbidity-field', name: 'Turbidity (field)', unit: 'NTU', default: false },
  { analyte: 'ph-in-lab', name: 'pH (lab)', unit: '', default: false },
  // { analyte: 'ph-in-field', name: 'pH (field)', unit: '', default: false },
  { analyte: 'electrical-conductivity-in-field', name: 'Electrical Conductivity', unit: 'mS/m', default: true },
  { analyte: 'total-suspended-solids', name: 'Total Suspended Solids', unit: 'g/m³', default: false },
  { analyte: 'chloride', name: 'Chloride', unit: 'g/m³', default: false },
  { analyte: 'total-ammoniacal-nitrogen', name: 'Total Ammoniacal-N', unit: 'g/m³', default: false },
  // { analyte: 'nitrite-n', name: 'Nitrite-N', unit: 'g/m³' },
  // { analyte: 'nitrate-n', name: 'Nitrate-N', unit: 'g/m³' },
  // { analyte: 'nitrate-+-nitrite-nitrogen', name: 'Nitrate-N + Nitrite-N', unit: 'g/m³' },
  { analyte: 'total-phosphorus', name: 'Total Phosphorus', unit: 'g/m³', default: false },
  { analyte: 'biochemical-oxygen-on-demand', name: 'BOD as O2', unit: 'g/m³', default: false },
  { analyte: 'chemical-oxygen-demand', name: 'COD as O2', unit: 'g/m³', default: false },
  { analyte: 'dissolved-oxygen-saturation-in-field', name: 'DO-field', unit: '%', default: false },
  { analyte: 'total-alkalinity', name: 'Alkalinity', unit: 'g/m³', default: false },
];

/**
 * Weather analyte properties
 */
export const analytesForWeather = [
  {
    analyte: 'sample-temperature-c',
    name: 'Temperature',
    unit: '°C',
    color: color[5],
    colorRGBA: color[5],
    default: true,
  },
  {
    analyte: 'precipitation',
    name: 'Precipitation',
    unit: 'mm/hr',
    color: color[1],
    colorRGBA: color[1],
    default: true,
  },
  {
    analyte: 'wind-speed',
    name: 'Wind Speed',
    unit: 'm/sec',
    color: '#00a7ca',
    colorRGBA: 'rgba(0, 167, 202, 1)',
    default: true,
  },
  {
    analyte: 'wind-direction',
    name: 'Wind Direction',
    unit: '°',
    color: '#8c7ebb',
    colorRGBA: 'rgba(140, 126, 187, 1)',
    default: true,
  },
];

/**
 * Gets the analyte property (name, unit, or label) for a given analyte.
 * @param name The analyte identifier.
 * @param property The property to retrieve: 'name', 'unit', or 'label'.
 * @returns The requested analyte property.
 */
export const getAnalyteProp = (name: string, property: 'name' | 'unit' | 'label' | 'color' | 'colorRGBA') => {
  const analyte = analytesForWeather.find((a) => a.analyte === name);
  if (!analyte) return property === 'label' ? name : '';
  if (property === 'label') return `${analyte.name} (${analyte.unit ?? ''})`;
  if (property === 'color') return analyte.color ?? '';
  if (property === 'colorRGBA') return analyte.colorRGBA ?? '';
  return analyte[property] ?? '';
};

/**
 * Wind Speed Unit for Waste Management
 */
const windSpeedUnit = getAnalyteProp(weatherAnalyteId.windSpeed, 'unit');

/**
 * Wind Speed Ranges for Waste Management
 *
 * Strongest winds are represented by warm, dark, or intense colors
 * Coolest winds are represented by light, cool colors
 */
export const WindSpeedRangesWM = [
  { name: `0 - 4 ${windSpeedUnit}`, min: 0, max: 4, color: '#0395ca' },
  { name: `4 - 6 ${windSpeedUnit}`, min: 4, max: 6, color: '#00b1d8' },
  { name: `6 - 10 ${windSpeedUnit}`, min: 6, max: 10, color: '#66c39b' },
  { name: `10 - 15 ${windSpeedUnit}`, min: 10, max: 15, color: '#FDD43A' },
  { name: `15 - 20 ${windSpeedUnit}`, min: 15, max: 20, color: '#f26b4b' },
  { name: `20 - 25 ${windSpeedUnit}`, min: 20, max: 25, color: '#ee4891' },
  { name: `40 ${windSpeedUnit}`, min: 40, max: 40, color: '#615097' },
];

/**
 * Formats the precipitation value for the tooltip.
 *
 * Multiplies values by 100 so this is displayed at the correct scale for mm/hr units
 *
 * @param value The precipitation value.
 * @param toPrecision The number of decimal places to include.
 * @returns The formatted precipitation value.
 */
export const formatPrecipitation = (value: number, toPrecision?: number): string => {
  return parseFloat(Number(value * 100).toFixed(toPrecision)).toString();
};

/**
 * Ground Water Relative Water Level Mapping for ARL Hoteo site
 */
const gwAnalyteRelativeWaterLevel = {
  analyte: 'relative-water-level',
  name: 'Water Level (Relative)',
  unit: 'm',
};

/**
 * Surface Water Depth Level Mapping for ARL Hoteo site
 */
const swAnalyteRelativeWaterLevel = {
  analyte: 'relative-water-level',
  name: 'Depth',
  unit: 'm',
};

/**
 * Stream Flow Analyte Mapping for ARL Hoteo site
 */
const sfAnalyteStreamFlow = {
  analyte: 'stream-flow',
  name: 'Stream Flow',
  unit: 'L/s',
};

const analytesForSurfacewaterGroupedByUnit = analytesForSurfacewater.reduce((acc, curr) => {
  const unit = curr.unit || 'no unit';
  const existingUnit = acc.find((item) => item.unit === unit);
  if (existingUnit) {
    existingUnit.analytes.push(curr);
  } else {
    acc.push({ unit, analytes: [curr] });
  }
  return acc;
}, [] as Array<{ unit: string; analytes: (typeof analytesForSurfacewater)[0][] }>);

/**
 * Ground Water Sources Mapping for ARL Hoteo site
 */
const gwSourcesARLHoteo = gwSourceIdsARLHoteo.map(
  (groundWaterSource) =>
    ({
      environmentalDataSourceIdentifier: groundWaterSource.source,
      analyteIdentifier: gwAnalyteRelativeWaterLevel.analyte,
      valueField: 'avg' as const,
      label: gwAnalyteRelativeWaterLevel.unit,
    } as BasicChartDataSource)
);

/**
 * Surface Water Sources Mapping for ARL Hoteo site
 */
const swSourcesARLHoteo = locationsAndSourcesSW.map(
  (surfaceWaterSource) =>
    ({
      environmentalDataSourceIdentifier: surfaceWaterSource.source,
      analyteIdentifier: swAnalyteRelativeWaterLevel.analyte,
      valueField: 'avg' as const,
      label: swAnalyteRelativeWaterLevel.unit,
    } as BasicChartDataSource)
);

/**
 * Stream Flow Mapping for ARL Hoteo site
 */
const sfSourcesARLHoteo = locationsAndSourcesSF.map(
  (streamFlowSource) =>
    ({
      environmentalDataSourceIdentifier: streamFlowSource.source,
      analyteIdentifier: sfAnalyteStreamFlow.analyte,
      valueField: 'avg' as const,
      label: sfAnalyteStreamFlow.unit,
    } as BasicChartDataSource)
);

/**
 * Ground Water ARL Hoteo Colors mapping
 */
const gwColorsARLHoteo = gwSourcesARLHoteo.map((_, i) => color[i % color.length]);
/**
 * Ground Water ARL Hoteo series mapping
 */
const gwSeriesDataARLHoteo = gwSourcesARLHoteo.map((dataSource) => dataSource.environmentalDataSourceIdentifier);
/**
 * Ground Water ARL Hoteo label mapping
 */
const gwUniqueLabelsARLHoteo = Array.from(new Set(gwSourcesARLHoteo.map((dataSource) => dataSource.label)));
/**
 * Surface Water ARL Hoteo Colors mapping
 */
const swColorsARLHoteo = swSourcesARLHoteo.map((_, i) => color[i % color.length]);
/**
 * Surface Water ARL Hoteo series mapping
 */
const swSeriesDataARLHoteo = swSourcesARLHoteo.map((dataSource) => dataSource.environmentalDataSourceIdentifier);
/**
 * Surface Water ARL Hoteo label mapping
 */
const swUniqueLabelsARLHoteo = Array.from(new Set(swSourcesARLHoteo.map((dataSource) => dataSource.label)));
/**
 * Stream Flow ARL Hoteo label mapping
 */
const sfUniqueLabelsARLHoteo = Array.from(new Set(sfSourcesARLHoteo.map((dataSource) => dataSource.label)));
/**
 * Stream Flow ARL Hoteo series mapping
 */
const sfSeriesDataARLHoteo = sfSourcesARLHoteo.map((dataSource) => dataSource.environmentalDataSourceIdentifier);
/**
 * Stream Flow ARL Hoteo Colors mapping
 */
const sfColorsARLHoteo = sfSourcesARLHoteo.map((_, i) => color[i % color.length]);

/**
 * Ground Water tooltip value formatter
 * @param value series value
 * @returns formatted tooltip string
 */
const gwToolTipValueFormatter = (value: number) => {
  const i = (this as any)?.seriesIndex ?? 0;
  return value === undefined ? '-' : `${value} ${gwSourcesARLHoteo[i].label}`;
};

/**
 * Wind Direction tooltip formatter
 * @param params The tooltip parameters
 * @returns The formatted tooltip string
 */
const windDirectionToolTipFormatter = (params: any) => {
  const value = params.value;
  return value === undefined ? '-' : `${value.toFixed(2)}%`;
};

const createGroundwaterChemistryConfig = (
  analyteIdentifier: string,
  title: string,
  subtitle: string
): BasicCartesianChartConfig => {
  const found = analytesForGroundwater.find((a) => a.analyte === analyteIdentifier);
  if (!found) {
    throw new Error(`Analyte identifier ${analyteIdentifier} not found in analytesForSurfacewater.`);
  }

  return {
    echartsConfig: {
      ...BasicCartesianChartEchartConfigTemplate,
      color,
      grid: {
        top: '25%',
        left: '7%',
        right: '7%',
        bottom: '6%',
        containLabel: true,
      },
      legend: {
        padding: [10, 80, 10, 80], // top, right, bottom, left
      },
      yAxis: [
        {
          ...BasicCartesianChartAxisTemplate,
          name: `${found.name}\n${found.unit}`,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#8c7ebb',
            },
          },
          axisLabel: {
            color: '#8c7ebb',
          },
          max: (value) => {
            return value.max + 20; // or value.max * 1.1 for 10% padding
          },
        },
      ],
      series: locationsAndSourcesGW.map((entry) => ({
        type: 'line',
        yAxisIndex: 0,
        name: entry.label,
        symbolSize: 5,
        connectNulls: true,
      })),
    },
    dataConfig: {
      title: title,
      subtitle: subtitle,
      widgetId: 1,
      domain: 'wai',
      dataSources: locationsAndSourcesGW.map((entry) => ({
        environmentalDataSourceIdentifier: entry.source,
        analyteIdentifier: analyteIdentifier,
        valueField: 'avg',
        label: entry.label,
      })),
    },
  };
};

export const COD = createGroundwaterChemistryConfig(
  'chemical-oxygen-demand',
  'Baseline Chemistry data – Bores – Chemical Oxygen Demand',
  'Threshold limit = 2000 g/m³'
);

export const CDM = createGroundwaterChemistryConfig(
  'electrical-conductivity-in-lab',
  'Baseline Chemistry data – Bores – Electrical Conductivity',
  ''
);

export const Temperature = createGroundwaterChemistryConfig(
  'sample-temperature-c',
  'Baseline Chemistry data – Bores – Temperature',
  ''
);

export const pH = createGroundwaterChemistryConfig('ph-in-lab', 'Baseline Chemistry data – Bores – pH', '');

export const Alkalinity = createGroundwaterChemistryConfig(
  'total-alkalinity',
  'Baseline Chemistry data – Bores – Alkalinity',
  ''
);

export const Arsenic = createGroundwaterChemistryConfig(
  'dissolved-arsenic',
  'Baseline Chemistry data – Bores – Arsenic',
  ''
);

export const Copper = createGroundwaterChemistryConfig(
  'dissolved-copper',
  'Baseline Chemistry data – Bores – Copper',
  ''
);

export const Iron = createGroundwaterChemistryConfig('dissolved-iron', 'Baseline Chemistry data – Bores – Iron', '');

export const Lead = createGroundwaterChemistryConfig('dissolved-lead', 'Baseline Chemistry data – Bores – Lead', '');

export const Zinc = createGroundwaterChemistryConfig('dissolved-zinc', 'Baseline Chemistry data – Bores – Zinc', '');

export const Magnesium = createGroundwaterChemistryConfig(
  'dissolved-magnesium',
  'Baseline Chemistry data – Bores – Magnesium',
  ''
);

export const Manganese = createGroundwaterChemistryConfig(
  'dissolved-manganese',
  'Baseline Chemistry data – Bores – Manganese',
  ''
);

export const Nickel = createGroundwaterChemistryConfig(
  'dissolved-nickel',
  'Baseline Chemistry data – Bores – Nickel',
  ''
);

export const NitrateNitrogen = createGroundwaterChemistryConfig(
  'nitrate-+-nitrite-nitrogen',
  'Baseline Chemistry data – Bores – Nitrate Nitrogen',
  ''
);

export const Potassium = createGroundwaterChemistryConfig(
  'dissolved-potassium',
  'Baseline Chemistry data – Bores – Potassium',
  ''
);

export const Sodium = createGroundwaterChemistryConfig('total-sodium', 'Baseline Chemistry data – Bores – Sodium', '');

export const Sulphate = createGroundwaterChemistryConfig('sulphate', 'Baseline Chemistry data – Bores – Sulphate', '');

export const Boron = createGroundwaterChemistryConfig('total-boron', 'Baseline Chemistry data – Bores – Boron', '');

export const Calcium = createGroundwaterChemistryConfig(
  'total-calcium',
  'Baseline Chemistry data – Bores – Calcium',
  ''
);

export const Chloride = createGroundwaterChemistryConfig('chloride', 'Baseline Chemistry data – Bores – Chloride', '');

export const TotalAmmoniacalNitrogen = createGroundwaterChemistryConfig(
  'total-ammoniacal-nitrogen',
  'Baseline Chemistry data – Bores – Total Ammoniacal Nitrogen',
  ''
);

export const TotalHardness = createGroundwaterChemistryConfig(
  'total-hardness',
  'Baseline Chemistry data – Bores – Total Hardness',
  ''
);

export const Bicarbonate = createGroundwaterChemistryConfig(
  'bicarbonate-at-25-degrees-c',
  'Baseline Chemistry data – Bores – Bicarbonate',
  ''
);

export const SumOfAnions = createGroundwaterChemistryConfig(
  'sum-of-anions',
  'Baseline Chemistry data – Bores – Sum of Anions',
  ''
);

export const SumOfCations = createGroundwaterChemistryConfig(
  'sum-of-cations',
  'Baseline Chemistry data – Bores – Sum of Cations',
  ''
);

export const SurfaceWaterConductivity: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color,
    grid: {
      top: '25%',
      left: '3%',
      right: '7%',
      bottom: '6%',
      containLabel: true,
    },
    legend: {
      padding: [10, 80, 10, 80], // top, right, bottom, left
      selected: locationsAndSourcesSW.reduce((acc, entry) => {
        acc[entry.label] = entry.default ?? true;
        return acc;
      }, {} as Record<string, boolean>),
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Conductivity\nmS/m',
        axisLine: {
          show: true,
          lineStyle: {
            color: '#8c7ebb',
          },
        },
        axisLabel: {
          formatter: (value: number) => {
            return value.toFixed(0);
          },
          color: '#8c7ebb',
        } as YAXisOption,
        max: (value) => {
          return value.max + 20; // or value.max * 1.1 for 10% padding
        },
      },
    ],
    series: locationsAndSourcesSW.map((entry) => ({
      type: 'line',
      yAxisIndex: 0,
      name: entry.label,
      symbolSize: 5,
      connectNulls: true,
    })),
  },
  dataConfig: {
    title: 'WAI - Surface Water - Conductivity Limits',
    subtitle: ' ',
    widgetId: 1,
    domain: 'wai',
    dataSources: locationsAndSourcesSW.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'electrical-conductivity-in-field',
      valueField: 'avg',
      label: entry.label,
    })),
  },
};

export const SurfaceWaterTurbidity: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color,
    grid: {
      top: '25%',
      left: '3%',
      right: '7%',
      bottom: '6%',
      containLabel: true,
    },
    legend: {
      padding: [10, 80, 10, 80], // top, right, bottom, left
      selected: locationsAndSourcesSW.reduce((acc, entry) => {
        acc[entry.label] = entry.default ?? true;
        return acc;
      }, {} as Record<string, boolean>),
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Turbidity\nNTU',
        axisLine: {
          show: true,
          lineStyle: {
            color: '#8c7ebb',
          },
        },
        axisLabel: {
          formatter: (value: number) => {
            return value.toFixed(0);
          },
          color: '#8c7ebb',
        } as YAXisOption,
        max: (value) => {
          return value.max + 20; // or value.max * 1.1 for 10% padding
        },
      },
    ],
    series: locationsAndSourcesSW.map((entry) => ({
      type: 'line',
      yAxisIndex: 0,
      name: entry.label,
      symbolSize: 5,
      connectNulls: true,
    })),
  },
  dataConfig: {
    title: 'WAI - Surface Water - Turbidity',
    subtitle: ' ',
    widgetId: 1,
    domain: 'wai',
    dataSources: locationsAndSourcesSW.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'turbidity',
      valueField: 'avg',
      label: entry.label,
    })),
  },
};

export const SurfaceWaterTemperature: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color,
    grid: {
      top: '25%',
      left: '3%',
      right: '7%',
      bottom: '6%',
      containLabel: true,
    },
    legend: {
      padding: [10, 80, 10, 80], // top, right, bottom, left
      selected: locationsAndSourcesSW.reduce((acc, entry) => {
        acc[entry.label] = entry.default ?? true;
        return acc;
      }, {} as Record<string, boolean>),
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        name: 'Temperature\n°C',
        axisLine: {
          show: true,
          lineStyle: {
            color: '#8c7ebb',
          },
        },
        axisLabel: {
          formatter: (value: number) => {
            return value.toPrecision(2);
          },
          color: '#8c7ebb',
        } as YAXisOption,
        max: (value) => {
          return value.max + 20; // or value.max * 1.1 for 10% padding
        },
      },
    ],
    series: locationsAndSourcesSW.map((entry) => ({
      type: 'line',
      yAxisIndex: 0,
      name: entry.label,
      symbolSize: 5,
      connectNulls: true,
    })),
  },
  dataConfig: {
    title: 'WAI - Surface Water - Water Temperature',
    subtitle: ' ',
    widgetId: 1,
    domain: 'wai',
    dataSources: locationsAndSourcesSW.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'water-temperature',
      valueField: 'avg',
      label: entry.label,
    })),
  },
};

export const PeriphytonClean: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color,
    grid: {
      top: '25%',
      left: '3%',
      right: '7%',
      bottom: '6%',
      containLabel: true,
    },
    legend: {
      padding: [10, 80, 10, 80],
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        type: 'value',
        name: 'Not Clean (%)',
        axisLine: {
          show: true,
          lineStyle: {
            color: '#8c7ebb',
          },
        },
        axisLabel: {
          color: '#8c7ebb',
        },
        max: 100,
      },
    ],
    series: locationsAndSourcesQMCI.map((entry) => ({
      type: 'line',
      yAxisIndex: 0,
      name: entry.label,
      symbolSize: 5,
      connectNulls: true,
      showSymbol: true,
    })),
  },
  dataConfig: {
    title: 'WAI - Periphyton % Not Clean Cover',
    subtitle: 'This shows how much of each streambed is not clean and free of algae.',
    widgetId: 2,
    domain: 'wai',
    groupBy: {
      grouping_type: 'time',
      period: 'quarter',
    },
    dataSources: locationsAndSourcesQMCI.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'periphyton-clean',
      valueField: 'avg',
      label: entry.label,
    })),
    postProcessingFunc: (data: any[]) => {
      // I would like to convert the value to be 100 - value
      Object.entries(data).forEach(([date, value]) => {
        data[date] = 100 - Number(value);
      });
      return data;
    },
  },
};

/**
 * Wetlands Vegetation Species Composition Pie Chart
 */
export const WetlandsVegetationSpeciesComposition: BasicPieChartConfig = {
  echartsConfig: {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        return `Score: ${params.value}`;
      },
    },
    toolbox: {
      show: true,
      feature: {
        mark: { show: true },
        dataView: {
          show: true,
          readOnly: false,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        restore: { show: true },
        saveAsImage: { show: true },
      },
    },
    series: sourcesVegetationGrid.map((entry) => ({
      type: 'pie',
      name: entry.label,
      center: ['50%', '50%'],
      radius: ['0%', '70%'],
    })),
  },
  dataConfig: {
    title: 'Wetlands Vegetation Species Composition',
    subtitle: 'Show the proportion of native, exotic, and species of uncertain origin recorded during the survey.',
    widgetId: 1,
    domain: 'wai',
    showBoundary: false,
    boundary: 0,
    dataSources: [
      {
        environmentalDataSourceIdentifier: 'all',
        analyteIdentifier: 'species-count',
        valueField: 'avg',
        label: 'ALL',
        entityNames: WetlandsVegetationSpecies,
      },
    ],
  },
};

/**
 * Wetlands Vegetation Species Richness Bar Chart
 *
 * - Stacked bar chart showing the species richness of wetlands vegetation categorized into shrubs, saplings, and trees.
 * - Each bar represents a species, with segments indicating the count of shrubs, saplings, and trees for that species.
 * - The x-axis shows the count, and the y-axis shows the species names.
 * - Tooltips provide detailed information about the counts for each species.
 * - Legend identifies the different vegetation categories.
 */
export const WetlandsVegetationSpeciesRichness: AggregateCartesianChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    // color: color.slice(0, 3),
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: false,
          optionToContent: (option: any): string | HTMLElement =>
            formatWetlandVegetationSpeciesDV(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('wetlands-vegetation-species-richness'),
          pixelRatio: 2,
        },
      },
    },
    tooltip: {
      trigger: 'item',
      // axisPointer: { type: 'shadow' },
      // formatter: showAllNotEmptyValuesFormatter,
    },
    legend: {
      top: 10,
      // data: ['Shrub count', 'Sapling count', 'Tree count'],
    },
    grid: {
      left: '0',
      right: '7%',
      top: '18%',
      bottom: '0',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        formatter: formatLongLabel,
      },
    },
    yAxis: [
      {
        ...BasicBarChartYAxisTemplate,
        type: 'value',
        name: 'Count',
        axisLabel: {
          color: BasicBarChartColourPalette[0],
        },
      },
      {
        type: 'value',
        min: 0,
        max: 100,
        name: 'Native Species %',
        axisLine: {
          show: true,
          lineStyle: {
            color: color[1],
          },
        },
        axisLabel: {
          color: color[1],
          formatter: (value: number) => `${value}%`,
        },
      },
    ],
    series: [] as SeriesOption[],
  },
  dataConfig: {
    title: 'Baseline Wetland Vegetation Monitoring - Species Richness',
    subtitle:
      'Wetland vegetation counts taken during the 2023 survey, with bars showing species richness and vegetation types across treatment areas.',
    widgetId: 5,
    domain: 'wai',
    showBoundary: true,
    dataSources: [
      {
        environmentalDataSourceIdentifier: 'all',
        analyteIdentifier: 'species-count',
        valueField: 'sum',
        label: 'Wetlands vegetation species richness',
        entityNames: WetlandsVegetationSpecies,
      },
    ],
    mapDataToSeries: wetlandsVegetationSpeciesMapper,
  },
};

export const Macrophytes: BasicPieChartConfig = {
  echartsConfig: {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        return `Score: ${params.value}`;
      },
    },
    toolbox: {
      show: true,
      feature: {
        mark: { show: true },
        dataView: {
          show: true,
          readOnly: false,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        restore: { show: true },
        saveAsImage: { show: true },
      },
    },
    series: sourcesMacrophyteLocations.map((entry) => ({
      type: 'pie',
      name: entry.label,
      center: ['50%', '50%'],
      radius: ['0%', '70%'],
    })),
  },
  dataConfig: {
    title: 'WAI - Macrophyte Species Composition - Ec5',
    subtitle:
      'Shows the proportion of native, exotic, and species of uncertain origin recorded during the most recent survey.',
    widgetId: 1,
    domain: 'wai',
    showBoundary: false,
    boundary: 0,
    dataSources: sourcesMacrophyteLocations.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'species-cover-percentage',
      valueField: 'avg',
      label: entry.label,
    })),
  },
};

export const MacrophytesBT: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color: color.slice(0, 5),
    toolbox: {
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('macrophytes-cover'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: [], // Data will be set in basic-bar-chart
      axisLine: {
        lineStyle: {
          color: BasicBarChartColourPalette[0],
        },
      },
      axisLabel: {
        rotate: 45,
        color: BasicBarChartColourPalette[0],
        rich: {
          bold: {
            fontWeight: 'bold',
          },
        },
      },
    },
  },
  dataConfig: {
    title: 'WAI - Macrophyte % Cover by Species - Ec5',
    subtitle: 'Shows the average percentage cover of each macrophyte species recorded over time.',
    widgetId: 2,
    domain: 'wai',
    groupBy: {
      grouping_type: 'time',
      period: 'quarter',
    },
    showBoundary: true,
    boundary: 0,
    dataSources: sourcesMacrophyteLocations.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'species-cover-percentage',
      valueField: 'sum',
      label: entry.label,
    })),
  },
};

export const MacrophytesBT2: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color: color.slice(0, 5),
    toolbox: {
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('macrophytes-cover'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: [], // Data will be set in basic-bar-chart
      axisLine: {
        lineStyle: {
          color: BasicBarChartColourPalette[0],
        },
      },
      axisLabel: {
        rotate: 45,
        color: BasicBarChartColourPalette[0],
        rich: {
          bold: {
            fontWeight: 'bold',
          },
        },
      },
    },
  },
  dataConfig: {
    title: 'WAI - Macrophyte % Cover by Species - Ec5',
    subtitle: 'Shows the average percentage cover of each macrophyte species recorded over time.',
    widgetId: 2,
    domain: 'wai',
    groupBy: {
      grouping_type: 'time',
      period: 'quarter',
    },
    showBoundary: true,
    boundary: 0,
    dataSources: sourcesMacrophyteLocations.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'species-cover-percentage',
      valueField: 'sum',
      label: entry.label,
    })),
  },
};

/**
 * Lizard 2023 Survey Results stacked bar chart.
 * Displays the count of lizard species recorded at each transect, with vegetation shown in the tooltip.
 */
export const Lizard2023SurveyResults: AggregateCartesianChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color,
    grid: {
      top: '22%',
      left: '6%',
      right: '6%',
      bottom: '12%',
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: lizardSurveyTooltipFormatter,
    },
    legend: {
      ...BasicBarChartEchartConfigTemplate.legend,
      type: 'scroll',
      top: 10,
    },
    xAxis: {
      ...(BasicBarChartEchartConfigTemplate.xAxis as any),
      type: 'category',
      boundaryGap: true,
      axisLabel: {
        ...((BasicBarChartEchartConfigTemplate.xAxis as any)?.axisLabel ?? {}),
        interval: 0,
        rotate: 0,
      },
    },
    yAxis: [
      {
        ...BasicBarChartYAxisTemplate,
        type: 'value',
        name: 'Count',
        axisLabel: {
          ...(BasicBarChartYAxisTemplate.axisLabel ?? {}),
          color: BasicBarChartColourPalette[0],
        },
      },
    ],
    series: [] as SeriesOption[],
  },
  dataConfig: {
    title: 'Baseline Lizard Monitoring',
    subtitle:
      'Lizard counts recorded during the 2023 survey, across all gecko and lizard transects, illustrating relative lizard abundance detected in each survey transect across site.',
    widgetId: 10,
    domain: 'wai',
    showBoundary: true,
    boundary: 0,
    formatXAxisAsDate: false,
    categoricalAxis: 'x',
    dataSources: [
      {
        environmentalDataSourceIdentifier: 'all',
        analyteIdentifier: 'species-count',
        valueField: 'sum',
        label: 'Lizard 2023 Survey Results',
        entityNames: ['common-skink', 'gecko-unknown', 'pacific-gecko', 'forest-gecko', 'elegant-gecko'],
      },
    ],
    mapDataToSeries: mapLizardSurveyData,
  },
};

export const SurfacewaterSW1A: BasicCartesianChartConfig = {
  ...createSWChemistryConfig('arl_sw1a', 'WAI - SW1A (Main Landfill Catchment) – Chemistry'),
};

export const SurfacewaterSW2A: BasicCartesianChartConfig = {
  ...createSWChemistryConfig('arl_sw2a', 'WAI - SW2A (Main Access Catchment) - Chemistry'),
};

export const SurfacewaterSW3A: BasicCartesianChartConfig = {
  ...createSWChemistryConfig('arl_sw3a', 'WAI - SW3A (Landfill Valley Catchment) – Chemistry'),
};

export const SurfacewaterSW4A: BasicCartesianChartConfig = {
  ...createSWChemistryConfig('arl_sw4a', 'WAI - SW4A (Landfill Valley Catchment) - Chemistry'),
};

export const SurfacewaterSW5A: BasicCartesianChartConfig = {
  ...createSWChemistryConfig('arl_sw5a', 'WAI - SW5A (Clay Borrow Stockpile Catchment) - Chemistry'),
};

export const SurfacewaterH10: BasicCartesianChartConfig = {
  ...createSWChemistryConfig('arl_h10', 'WAI - H10 (Waitarere Upstream Catchment) - Chemistry'),
};

export const SurfacewaterH11: BasicCartesianChartConfig = {
  ...createSWChemistryConfig('arl_h11', 'WAI - H11 (Waitarere Upstream Catchment) - Chemistry'),
};

/**
 * Factory to create the repeated Surfacewater BasicCartesianChartConfig.
 * Allows for identical ECharts configuration, while allowing different titles and source ids.
 */
function createSWChemistryConfig(environmentalDataSourceIdentifier: string, title: string): BasicCartesianChartConfig {
  const echartsConfig: EChartsOption = {
    color,
    grid: {
      top: '30%',
      left: '11%',
      right: '11%',
      bottom: '6%',
      containLabel: true,
    },
    legend: {
      padding: [10, 10, 10, 10], // top, right, bottom, left
      show: true,
      data: analytesForSurfacewater.map((aa) => aa.name),
      selected: analytesForSurfacewater.reduce((acc, a) => {
        acc[a.name] = a.default;
        return acc;
      }, {} as Record<string, boolean>),
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
      },
    ],
    yAxis: analytesForSurfacewaterGroupedByUnit.map((entry, index) => ({
      type: 'value',
      position: index % 2 === 0 ? 'left' : 'right',
      offset: 40 * (index / 2),
      name: entry.unit,
      axisLine: {
        show: true,
        lineStyle: {
          color: color[1 + analytesForSurfacewaterGroupedByUnit.length],
        },
      },
      axisLabel: {
        color: color[1 + analytesForSurfacewaterGroupedByUnit.length],
      },
    })),
    series: analytesForSurfacewaterGroupedByUnit.reduce((acc, curr, i) => {
      const s = curr.analytes.map((entry, index) => ({
        type: 'line',
        name: entry.name,
        stack: 'Total',
        smooth: true,
        lineStyle: {
          width: 0,
        },
        areaStyle: {
          opacity: 0.8,
          color: color[acc.length + index],
        },
        yAxisIndex: i,
        emphasis: {
          focus: 'series',
        },
        showSymbol: false,
        connectNulls: true,
      })) as [];
      return [...acc, ...s];
    }, []),
  };

  return {
    echartsConfig,
    dataConfig: {
      title,
      widgetId: 1,
      domain: 'wai',
      dataSources: analytesForSurfacewater.map((entry) => ({
        environmentalDataSourceIdentifier,
        analyteIdentifier: entry.analyte,
        valueField: 'avg',
        label: entry.name,
      })),
      displayLabelOptions: {
        legendLabelPriority: ['configLabel', 'analyte', 'humanReadableName', 'entity', 'sourceId'],
        includeLocation: true,
        includeAnalyte: false,
      },
      preserveConfiguredDataSourceLabels: true,
    },
  } as BasicCartesianChartConfig;
}

export const WeatherWMChartConfig: EChartsOption = {
  ...BasicCartesianChartEchartConfigTemplate,
  legend: {
    data: [
      getAnalyteProp(weatherAnalyteId.temperature, 'name'),
      getAnalyteProp(weatherAnalyteId.precipitation, 'name'),
    ],
    top: 10,
  },
  color: [
    getAnalyteProp(weatherAnalyteId.temperature, 'color'),
    getAnalyteProp(weatherAnalyteId.precipitation, 'color'),
  ],
  grid: {
    top: '25%',
    left: '5%',
    right: '5%',
    bottom: '25%',
    containLabel: true,
  },
  toolbox: {
    left: 'right',
    itemSize: 18,
    top: 0,
    feature: {
      restore: {},
      saveAsImage: {
        show: true,
        name: getTimestampedFileNameForWidget('weather-temperature-precipitation'),
      },
    },
  },
  xAxis: {
    type: 'time',
    boundaryGap: [0, 0],
    splitLine: {
      show: false,
    },
    axisPointer: {
      type: 'shadow',
    },
  },
  yAxis: [
    {
      type: 'value',
      name: `${getAnalyteProp(weatherAnalyteId.temperature, 'name')} (${getAnalyteProp(
        weatherAnalyteId.temperature,
        'unit'
      )})`,
      position: 'left',
      interval: 5,
      max: 35,
      axisLine: {
        show: false,
        lineStyle: {
          color: getAnalyteProp(weatherAnalyteId.temperature, 'color'),
        },
      },
      axisLabel: {
        formatter: '{value}',
        color: getAnalyteProp(weatherAnalyteId.temperature, 'color'),
      },
      splitLine: {
        show: false,
      },
    },
    {
      type: 'value',
      name: `${getAnalyteProp(weatherAnalyteId.precipitation, 'name')} (${getAnalyteProp(
        weatherAnalyteId.precipitation,
        'unit'
      )})`,
      position: 'right',
      inverse: true,
      nameLocation: 'start',
      nameGap: 10,
      axisLine: {
        show: false,
        lineStyle: {
          color: getAnalyteProp(weatherAnalyteId.precipitation, 'color'),
        },
      },
      axisLabel: {
        color: getAnalyteProp(weatherAnalyteId.precipitation, 'color'),
        formatter: (value: number) => formatPrecipitation(value, 2),
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getAnalyteProp(weatherAnalyteId.precipitation, 'color'),
        },
      },
    },
  ],
  dataZoom: [
    {
      type: 'slider',
      bottom: 50,
      height: 20,
      throttle: 50,
      minValueSpan: 43200000, // 12 hours max zoom
    },
  ],
  visualMap: {
    show: false,
  },
  series: sourcesWeatherWM
    .filter(
      (source) =>
        source.analyteId === weatherAnalyteId.temperature || source.analyteId === weatherAnalyteId.precipitation
    )
    .map((source) => {
      const analyteName = getAnalyteProp(source.analyteId, 'name');
      switch (source.analyteId) {
        case weatherAnalyteId.temperature:
          return {
            name: analyteName,
            type: 'line',
            yAxisIndex: 0,
            showSymbol: false,
            emphasis: {
              focus: 'series',
              itemStyle: {
                color: getAnalyteProp(weatherAnalyteId.temperature, 'color'),
                borderColor: '#fff', // outline color on hover
                borderWidth: 2, // outline width
              },
              lineStyle: { color: getAnalyteProp(weatherAnalyteId.temperature, 'color') },
            },
            lineStyle: { color: getAnalyteProp(weatherAnalyteId.temperature, 'color') },
            itemStyle: { color: getAnalyteProp(weatherAnalyteId.temperature, 'color') },
            encode: { x: 'date_time', y: 'temperature' },
            symbol: 'circle',
            symbolSize: 6,
            showAllSymbol: true, // ensures symbol is shown on hover
          };
        case weatherAnalyteId.precipitation:
          return {
            name: analyteName,
            type: 'line',
            yAxisIndex: 1,
            showSymbol: false,
            smooth: true,
            emphasis: {
              focus: 'series',
              itemStyle: {
                color: getAnalyteProp(weatherAnalyteId.precipitation, 'color'),
                borderColor: '#fff', // outline color on hover
                borderWidth: 2, // outline width
              },
              lineStyle: { color: getAnalyteProp(weatherAnalyteId.precipitation, 'color') },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: 'rgba(255,255,255,0.3)' },
                  { offset: 1, color: getAnalyteProp(weatherAnalyteId.precipitation, 'colorRGBA') },
                ]),
              },
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(255,255,255,0.3)' },
                { offset: 1, color: getAnalyteProp(weatherAnalyteId.precipitation, 'colorRGBA') },
              ]),
            },
            lineStyle: { color: getAnalyteProp(weatherAnalyteId.precipitation, 'color') },
            itemStyle: { color: getAnalyteProp(weatherAnalyteId.precipitation, 'color') },
            encode: { x: 'date_time', y: 'precipitation' },
            symbol: 'circle',
            symbolSize: 6,
            showAllSymbol: true,
          };
        default:
          return undefined;
      }
    }) as SeriesOption[],
};

export const WeatherWM: BasicCartesianChartConfig = {
  echartsConfig: {
    ...WeatherWMChartConfig,
  },
  dataConfig: {
    title: 'Weather - Temperature and Precipitation',
    widgetId: 1,
    domain: 'wai',
    dataSources: sourcesWeatherWM.map((weatherSources) => ({
      environmentalDataSourceIdentifier: weatherSources.source,
      analyteIdentifier: weatherSources.analyteId,
      valueField: 'avg',
      label: getAnalyteProp(weatherSources.analyteId, 'label'),
    })),
  },
};

export const WindWM: BasicPolarChartConfig = {
  echartsConfig: {
    toolbox: {
      left: 'right',
      itemSize: 18,
      top: 0,
      feature: {
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('weather-wind-speed-direction'),
        },
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: '#ffffff',
      textStyle: {
        color: '#000000',
        fontWeight: 'normal',
      },
    },

    title: {
      text: `${getAnalyteProp(weatherAnalyteId.windSpeed, 'name')} (avg)`,
      left: 0,
      top: 0,
      textStyle: {
        color: '#000000',
        fontSize: 14,
      },
    },

    legend: {
      show: true,
      orient: 'vertical',
      left: 0,
      top: 40,
      data: WindSpeedRangesWM.map((r) => r.name),
      textStyle: {
        color: '#000000',
      },
    },

    polar: {
      center: ['50%', '50%'],
      radius: ['15%', '85%'],
    },

    radiusAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} %',
        color: '#ccc',
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(255, 255, 255, 1)',
        },
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(255, 255, 255, 1)',
        },
      },
    },

    angleAxis: {
      type: 'category',
      data: windDirectionCompassLabels,
      startAngle: 100,
    },

    series: WindSpeedRangesWM.map((range) => ({
      name: range.name,
      type: 'bar',
      coordinateSystem: 'polar',
      stack: 'windData',
      emphasis: { focus: 'series' },
      itemStyle: {
        color: range.color,
      },
      data: [], // Data will be set in basic-polar-bar-chart
    })),
  },
  dataConfig: {
    title: 'Weather - Wind',
    subtitle:
      'This wind rose shows how often the wind blows from each direction, and how strong it is, during the selected time period. The length of each bar reflects frequency, and the colours indicate wind speed. To change the time period displayed, use the date filter on the left of StreamDash.',
    widgetId: 2,
    domain: 'wai',
    showBoundary: false,
    boundary: 0,
    dataSources: sourcesWindWM.map((windSources) => ({
      environmentalDataSourceIdentifier: windSources.source,
      analyteIdentifier: windSources.analyteId,
      valueField: 'avg',
      label: getAnalyteProp(windSources.analyteId, 'label'),
    })),
  },
};

export const ARLGroundWaterLevels: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color: gwColorsARLHoteo,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985',
        },
      },
    },
    toolbox: {
      top: 40,
      feature: {
        dataView: {
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {},
      },
    },
    yAxis: gwUniqueLabelsARLHoteo.map((label) => ({
      type: 'value',
      axisLabel: {
        formatter: `{value} ${label}`,
      },
    })) as YAXisOption[],
    series: (gwSeriesDataARLHoteo ?? []).map((name, i) => ({
      name: gwSourceIdsARLHoteo[i].label,
      type: 'line',
      smooth: true,
      lineStyle: { width: 1 },
      showSymbol: false,
      tooltip: {
        valueFormatter: gwToolTipValueFormatter,
      },
      areaStyle: {
        opacity: 0.8,
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${gwColorsARLHoteo[i]}80` }, // semi-transparent
          { offset: 1, color: `${gwColorsARLHoteo[i]}00` }, // transparent
        ]),
      },
      emphasis: { focus: 'series' },
    })) as SeriesOption[],
  },
  dataConfig: {
    title: 'WAI - Groundwater Water Levels',
    widgetId: 2,
    domain: 'wai',
    dataSources: gwSourcesARLHoteo,
  },
};

/**
 * ARL Surface Water Levels Chart
 *
 * Uses line chart to represent surface water levels at various locations
 * Data sources and labels are defined in locationsAndSourcesSW
 * Each location/source is represented as a separate series in the line chart
 * The chart includes a legend, tooltip, and toolbox for interactivity
 * The y-axis represents water level in meters
 * The x-axis represents time
 *
 * @type {BasicCartesianChartConfig}
 */
export const ARLSurfaceWaterLevels: BasicCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color: swColorsARLHoteo,
    legend: {
      padding: [10, 80, 10, 80], // top, right, bottom, left
      selected: locationsAndSourcesSW.reduce((acc, entry) => {
        acc[entry.label] = entry.default ?? true;
        return acc;
      }, {} as Record<string, boolean>),
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985',
        },
      },
    },
    toolbox: {
      top: 40,
      feature: {
        dataView: {
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {},
      },
    },
    yAxis: swUniqueLabelsARLHoteo.map((label) => ({
      type: 'value',
      axisLabel: {
        formatter: `{value} ${label}`,
      },
    })) as YAXisOption[],
    series: (swSeriesDataARLHoteo ?? []).map((name, i) => ({
      name: locationsAndSourcesSW[i].label,
      type: 'line',
      smooth: true,
      lineStyle: { width: 1 },
      showSymbol: false,
      tooltip: {
        valueFormatter: gwToolTipValueFormatter,
      },
      areaStyle: {
        opacity: 0.8,
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${swColorsARLHoteo[i]}80` }, // semi-transparent
          { offset: 1, color: `${swColorsARLHoteo[i]}00` }, // transparent
        ]),
      },
      emphasis: { focus: 'series' },
    })) as SeriesOption[],
  },
  dataConfig: {
    title: 'WAI - Surface Water Levels',
    widgetId: 2,
    domain: 'wai',
    dataSources: swSourcesARLHoteo,
  },
};

/**
 * ARL Stream Flow Chart
 *
 * Uses line chart to represent stream flow at various locations
 * Data sources and labels are defined in locationsAndSourcesSW
 * Each location/source is represented as a separate series in the line chart
 * The chart includes a legend, tooltip, and toolbox for interactivity
 * The y-axis represents stream flow in liters per second
 * The x-axis represents time
 *
 * @type {BasicCartesianChartConfig}
 */
export const ARLStreamFlow: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color: sfColorsARLHoteo,
    legend: {
      top: 10,
      padding: [8, 20, 8, 20],
      data: locationsAndSourcesSF.map((l) => l.label),
    },
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('arl-stream-flow'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: [],
      axisLine: {
        lineStyle: {
          color: BasicBarChartColourPalette[0],
        },
      },
      axisLabel: {
        rotate: 45,
        color: BasicBarChartColourPalette[0],
        rich: {
          bold: {
            fontWeight: 'bold',
          },
        },
      },
    },
    yAxis: [
      {
        ...BasicBarChartYAxisTemplate,
        type: 'value',
        name: 'Flow (L/s)',
      },
    ],
    series: (sfSeriesDataARLHoteo ?? []).map((name, i) => ({
      name: locationsAndSourcesSF[i].label,
      type: 'bar',
      barWidth: '5px',
      barGap: '5%',
      barCategoryGap: '-30%',
      emphasis: { focus: 'series' },
      animationDelay: function (idx) {
        return idx * 10;
      },
      animationEasing: 'elasticOut',
      animationDelayUpdate: function (idx) {
        return idx * 5;
      },
      encode: { x: 'date_time', y: 'avg' },
      data: [],
    })) as SeriesOption[],
  },
  dataConfig: {
    title: 'WAI - Stream Flow',
    widgetId: 2,
    domain: 'wai',
    showBoundary: true,
    boundary: 0,
    groupBy: null,
    displayLabelOptions: {
      legendLabelPriority: ['humanReadableName', 'configLabel', 'sourceId', 'analyte', 'entity'],
      includeLocation: true,
      includeAnalyte: false,
    },
    dataSources: sfSourcesARLHoteo,
  },
};

/**
 * Wetlands Sediment Height Chart
 *
 * Uses bar chart to represent sediment height at various locations
 * Data sources and labels are defined in locationsAndSourcesARLSediment
 * Each location/source is represented as a separate series in the bar chart
 * The chart includes a legend, tooltip, and toolbox for interactivity
 * The y-axis represents sediment height in cm
 * The x-axis represents time
 *
 * X-axis labels are formatted using ECharts rich text to show day/month and year
 *
 * @type {BasicBarChartDataConfig}
 */
export const WetlandsSediment: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color: color.slice(0, 5),
    legend: {
      top: 10,
      padding: [8, 20, 8, 20],
      data: locationsAndSourcesARLSediment.map((l) => l.label),
    },
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('wetlands-sediment-height'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: [], // Data will be set in basic-bar-chart
      axisLine: {
        lineStyle: {
          color: BasicBarChartColourPalette[0],
        },
      },

      axisLabel: {
        rotate: 45,
        color: BasicBarChartColourPalette[0],
        rich: {
          bold: {
            fontWeight: 'bold',
          },
        },
        formatter: (value: any) => {
          let mm = moment(value, 'DD/MM/yyyy', true);
          if (!mm.isValid()) {
            mm = moment(value);
          }
          if (!mm.isValid()) return '';
          const dayMonth = mm.format('DD/MM');
          const year = mm.format('YY');
          return `${dayMonth} '{bold|${year}}`;
        },
      },
    },
    yAxis: [
      {
        ...BasicBarChartYAxisTemplate,
        type: 'value',
        name: 'Height (cm)',
      },
    ],
    series: locationsAndSourcesARLSediment.map((entry) => {
      return {
        name: entry.label,
        type: 'bar',
        barWidth: '5px',
        barGap: '5%',
        barCategoryGap: '-30%',
        emphasis: { focus: 'series' },
        animationDelay: function (idx) {
          return idx * 10;
        },
        animationEasing: 'elasticOut',
        animationDelayUpdate: function (idx) {
          return idx * 5;
        },
        encode: { x: 'date_time', y: 'avg' },
        data: [],
      };
    }) as SeriesOption[],
  },
  dataConfig: {
    title: 'Baseline Wetland Sediment Monitoring',
    widgetId: 2,
    domain: 'wai',
    groupBy: {
      grouping_type: 'time',
      period: 'quarter',
    },
    showBoundary: true,
    boundary: 0,
    dataSources: locationsAndSourcesARLSediment.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'sediment-height-ruler-cm',
      valueField: 'avg',
      label: entry.label,
    })),
  },
};

const locationsAndSourcesMCISoftBottom = [
  { label: 'Ec3', source: 'arl_ec3' },
  { label: 'Ec5', source: 'arl_ec5' },
];

export const MacroinvertebrateCommunityIndexSoftBottomStreams: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color: color.slice(0, 2),
    grid: {
      top: '17%',
      left: '6%',
      right: '8%',
      bottom: '12%',
      containLabel: true,
    },
    legend: {
      top: 10,
      padding: [8, 20, 8, 20],
      data: locationsAndSourcesMCISoftBottom.map((l) => l.label),
    },
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('macroinvertebrate-community-index'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: [], // Data will be set in basic-bar-chart
      axisLine: {
        lineStyle: {
          color: BasicBarChartColourPalette[0],
        },
      },
      axisLabel: {
        rotate: 45,
        color: BasicBarChartColourPalette[0],
        rich: {
          bold: {
            fontWeight: 'bold',
          },
        },
      },
    },
    yAxis: [
      {
        ...BasicBarChartYAxisTemplate,
        type: 'value',
        name: 'QMCI-sb value',
      },
    ],
    series: locationsAndSourcesMCISoftBottom.map((entry) => {
      return {
        name: entry.label,
        type: 'bar',
        barWidth: '20%',
        barGap: '5%',
        barCategoryGap: '-30%',
        emphasis: { focus: 'series' },
        animationDelay: function (idx) {
          return idx * 10;
        },
        animationEasing: 'elasticOut',
        animationDelayUpdate: function (idx) {
          return idx * 5;
        },
        encode: { x: 'date_time', y: 'avg' },
        data: [],
      };
    }) as SeriesOption[],
  },
  dataConfig: {
    title: 'Macroinvertebrate Community Index - Soft-Bottom Streams',
    subtitle:
      'This widget uses the QMCI-sb (Quantitative Macroinvertebrate Community Index – soft-bottomed). The following quality classes should be utilised: >5.99 = Excellent (Clean water); 5.00-5.90 = Good (Doubtful quality or possible mild pollution); 4.00-4.00 = Fair (Probable moderate pollution); <4.00 = Poor (Probable severe pollution).',
    widgetId: 2,
    domain: 'wai',
    groupBy: {
      grouping_type: 'time',
      period: 'quarter',
    },
    showBoundary: true,
    boundary: 0,
    dataSources: locationsAndSourcesMCISoftBottom.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'qmci-sb',
      valueField: 'avg',
      label: entry.label,
    })),
  },
};

const locationsAndSourcesMCIHardBottom = [
  { label: 'Ec1', source: 'arl_ec1' },
  { label: 'Ec2', source: 'arl_ec2' },
  { label: 'Ec4', source: 'arl_ec4' },
];

export const MacroinvertebrateCommunityIndexHardBottomStreams: BasicBarChartConfig = {
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color: color.slice(0, 2),
    grid: {
      top: '17%',
      left: '6%',
      right: '8%',
      bottom: '12%',
      containLabel: true,
    },
    legend: {
      top: 10,
      padding: [8, 20, 8, 20],
      data: locationsAndSourcesMCIHardBottom.map((l) => l.label),
    },
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('macroinvertebrate-community-index'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: [], // Data will be set in basic-bar-chart
      axisLine: {
        lineStyle: {
          color: BasicBarChartColourPalette[0],
        },
      },
      axisLabel: {
        rotate: 45,
        color: BasicBarChartColourPalette[0],
        rich: {
          bold: {
            fontWeight: 'bold',
          },
        },
      },
    },
    yAxis: [
      {
        ...BasicBarChartYAxisTemplate,
        type: 'value',
        name: 'QMCI value',
      },
    ],
    series: locationsAndSourcesMCIHardBottom.map((entry) => {
      return {
        name: entry.label,
        type: 'bar',
        barWidth: '20%',
        barGap: '5%',
        barCategoryGap: '-30%',
        emphasis: { focus: 'series' },
        animationDelay: function (idx) {
          return idx * 10;
        },
        animationEasing: 'elasticOut',
        animationDelayUpdate: function (idx) {
          return idx * 5;
        },
        encode: { x: 'date_time', y: 'avg' },
        data: [],
      };
    }) as SeriesOption[],
  },
  dataConfig: {
    title: 'Macroinvertebrate Community Index - Hard-Bottom Streams',
    subtitle:
      'This widget uses the QMCI (Quantitative Macroinvertebrate Community Index – hard-bottomed). The following quality classes should be utilised: >5.99 = Excellent (Clean water); 5.00-5.90 = Good (Doubtful quality or possible mild pollution); 4.00-4.00 = Fair (Probable moderate pollution); <4.00 = Poor (Probable severe pollution).',
    widgetId: 2,
    domain: 'wai',
    groupBy: {
      grouping_type: 'time',
      period: 'quarter',
    },
    showBoundary: true,
    boundary: 0,
    dataSources: locationsAndSourcesMCIHardBottom.map((entry) => ({
      environmentalDataSourceIdentifier: entry.source,
      analyteIdentifier: 'qmci',
      valueField: 'avg',
      label: entry.label,
    })),
  },
};

export const TerrestrialVegetationComposition: AggregateCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color: [color[8], color[5], color[0], color[4], color[1]],
    grid: {
      top: '17%',
      left: '6%',
      right: '8%',
      bottom: '0',
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: terrestrialVegetationTooltipFormatter,
    },
    legend: {
      show: true,
      top: 10,
      data: [],
    },
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        magicType: {
          show: true,
          type: ['stack'],
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('terrestrial-vegetation-composition'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: [
      {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          formatter: formatLongLabel,
        },
      },
    ],
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        type: 'value',
        min: 0,
        max: 300,
        interval: 50,
        name: 'Vegetation Types',
        axisLine: {
          show: true,
          lineStyle: {
            color: color[8],
          },
        },
        axisLabel: {
          color: color[8],
        },
      },
      {
        type: 'value',
        min: 0,
        max: 100,
        name: 'Native Species %',
        axisLine: {
          show: true,
          lineStyle: {
            color: color[1],
          },
        },
        axisLabel: {
          color: color[1],
          formatter: (value: number) => `${value}%`,
        },
      },
    ],
  },
  dataConfig: {
    title: 'Terrestrial Vegetation Composition',
    subtitle:
      'Terrestrial vegetation counts taken during the 2023 survey, with bars showing vegetation types across treatment areas.',
    widgetId: 6,
    domain: 'wai',
    formatXAxisAsDate: false,
    boundary: 0.5,
    showBoundary: true,
    dataSources: [
      {
        environmentalDataSourceIdentifier: 'all',
        analyteIdentifier: 'species-count',
        valueField: 'avg',
        label: 'ALL',
        entityNames: terrestrialVegetationSpecies,
      },
    ],
    mapDataToSeries: mapTerrestrialVegetationData,
  },
};

export const TerrestrialBirdSpeciesCounts: AggregateCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color,
    grid: {
      top: '20%',
      left: '6%',
      right: '6%',
      bottom: '0%',
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      formatter: showAllNotEmptyValuesFormatter,
    },
    legend: {
      show: true,
      padding: [8, 80, 8, 80],
      top: 10,
      type: 'scroll',
    },
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('terrestrial-bird-species-counts'),
          pixelRatio: 2,
        },
      },
    },
    xAxis: [
      {
        type: 'category',
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          formatter: formatLongLabel,
        },
      },
    ],
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        type: 'value',
        min: 0,
        max: 500,
        interval: 50,
        name: 'Average Count',
        axisLine: {
          show: true,
          lineStyle: {
            color: color[8],
          },
        },
        axisLabel: {
          color: color[8],
        },
      },
    ],
  },
  dataConfig: {
    title: 'Terrestrial Bird Composition by Species',
    subtitle:
      'Terrestrial bird counts taken during the 2023 survey, by treatment area, with bars divided to show species composition.',
    widgetId: 7,
    domain: 'wai',
    formatXAxisAsDate: false,
    boundary: 0.5,
    showBoundary: true,
    dataSources: [
      {
        environmentalDataSourceIdentifier: 'all',
        analyteIdentifier: 'species-count',
        valueField: 'avg',
        label: 'ALL',
        entityNames: terrestrialBirdSpecies,
      },
    ],
    mapDataToSeries: mapTerrestrialBirdSpeciesData,
  },
};

const formatAxisLabelDecimal = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return n.toFixed(0);
};

export const HochstettersFrog: AggregateCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    color: [color[1], color[10]],

    tooltip: {
      trigger: 'axis',
      formatter: showAllNotEmptyValuesFormatter,
    },
    legend: {
      show: true,
      data: [],
    },
    xAxis: {
      type: 'category',
      axisLabel: {
        formatter: formatLongLabel,
      },
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        type: 'value',
        name: 'Number\nof Frogs',
        position: 'left',
        min: 0,
        max: 500,
        interval: 50,
        axisLabel: {
          formatter: formatAxisLabelDecimal,
          color: color[1],
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: color[1],
          },
        },
        axisPointer: {
          label: {
            backgroundColor: color[1],
            color: '#ffffff',
            formatter: formatAxisPointerLabelDecimal,
          },
        },
      },
    ],
  },
  dataConfig: {
    title: 'Baseline Frog Monitoring',
    subtitle: 'Frog counts recorded during the 2023 survey, across all sites and treatment areas.',
    widgetId: 1,
    domain: 'wai',
    showBoundary: true,
    boundary: 0,
    dataSources: [
      {
        environmentalDataSourceIdentifier: 'all',
        analyteIdentifier: 'species-count',
        valueField: 'avg',
        label: 'ALL',
        entityNames: ['pangokereia-hochstetter’s-frog'],
      },
    ],
    mapDataToSeries: convertFrogData,
  },
};

export const WetlandBirds: AggregateCartesianChartConfig = {
  echartsConfig: {
    ...BasicCartesianChartEchartConfigTemplate,
    grid: {
      top: '25%',
      left: '3%',
      right: '7%',
      bottom: '6%',
      containLabel: true,
    },
    color: [color[1], color[10]],
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: false,
          optionToContent: (option: any): string | HTMLElement => formatWetlandBirdSpeciesDV(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('wetlands-bird-observations'),
          pixelRatio: 2,
        },
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#999',
        },
      },
    },
    xAxis: {
      type: 'category',
      axisLabel: {
        formatter: formatLongLabel,
      },
    },
    yAxis: [
      {
        ...BasicCartesianChartAxisTemplate,
        type: 'value',
        name: 'Bird Count',
        position: 'left',
        min: 'dataMin',
        max: 'dataMax',
        axisLabel: {
          formatter: formatAxisLabelDecimal,
          color: color[1],
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: color[1],
          },
        },
        axisPointer: {
          label: {
            backgroundColor: color[1],
            color: '#ffffff',
            formatter: formatAxisPointerLabelDecimal,
          },
        },
      },
    ],
  },
  dataConfig: {
    title: 'Baseline Wetland Bird Monitoring',
    subtitle: 'Wetland Bird presence/absence recorded during the 2023 survey, across all treatment areas.',
    widgetId: 1,
    domain: 'wai',
    showBoundary: true,
    boundary: 5,
    dataSources: [
      {
        environmentalDataSourceIdentifier: 'all',
        analyteIdentifier: 'species-count',
        valueField: 'avg',
        label: 'ALL',
        entityNames: ['fernbird', 'spotless-crake'],
      },
    ],
    mapDataToSeries: wetlandsBirdSpeciesMapper,
  },
};

export const PeriphytonCoverCompositionEc1: BasicBarChartConfig = buildPeriphytonCoverCompositionConfig(
  'Ec1',
  'arl_ec1'
);
export const PeriphytonCoverCompositionEc2: BasicBarChartConfig = buildPeriphytonCoverCompositionConfig(
  'Ec2',
  'arl_ec2'
);
export const PeriphytonCoverCompositionEc4: BasicBarChartConfig = buildPeriphytonCoverCompositionConfig(
  'Ec4',
  'arl_ec4'
);
